// https://mokkapps.de/vue-tips/watch-slot-changes

/**
 * Monitors changes in the DOM contents of a specified slot using the browser MutationObserver API.
 *
 * This hook returns a reference to be assigned to the parent of a <slot /> element.
 * When the contents of the slot change, the provided callback function (onUpdateCallback) will be executed.
 *
 * @param onUpdateCallback - A callback function that runs when the contents of the slot change.
 * @returns A reference to the parent node of the <slot /> element. Assign this reference to the parent node to enable monitoring.
 */
export const useWatchSlotContentChange = (
  onUpdateCallback: () => void,
): Ref<Node | null> => {
  const observer: Ref<MutationObserver | null> = ref(null);
  const contentSlotRef: Ref<Node | null> = ref(null);

  onMounted(() => {
    observer.value = new MutationObserver(onUpdateCallback);

    if (contentSlotRef.value) {
      observer.value.observe(contentSlotRef.value, {
        childList: true,
        subtree: true,
      });
    }
  });

  onBeforeUnmount(() => {
    if (observer.value) {
      observer.value.disconnect();
    }
  });

  return contentSlotRef;
};
